import { useEffect, useState } from "react";
import { useLiveTasks, clearTasks, type LiveTask } from "../data/liveTasks.js";

/** live (running) / done / error → a color class. */
function statusClass(t: LiveTask): string {
  if (t.status === "error") return "dw-st-error";
  return t.endMs == null ? "dw-st-live" : "dw-st-done";
}

/** Running age for a live task, or total duration for a finished one. */
function ageMs(t: LiveTask): number {
  return (t.endMs ?? Date.now()) - t.startMs;
}

/** The task's name; query spans are still anonymous SQL → a placeholder. */
function taskName(t: LiveTask): string {
  return t.name || "SELECT...";
}

function fmtMs(ms: number): string {
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

/** Scroll to the embed that ran this task (same page only, for now). */
function scrollToEmbed(embedId: string | null): void {
  if (!embedId) return;
  const node = document.getElementById(embedId);
  if (!node) return; // not on this page / unmounted — no-op for now
  node.scrollIntoView({ block: "center", behavior: "smooth" });
  node.classList.add("dw-embed-flash");
  setTimeout(() => node.classList.remove("dw-embed-flash"), 1200);
}

/**
 * Live task list for this session, driven by the WebSocket push (no polling).
 * EXPERIMENT — renders alongside the legacy polling TasksCard for comparison.
 */
export function LiveTasksCard() {
  const tasks = useLiveTasks();

  // Tick while anything is running so live ages keep growing between WS pushes.
  const [, setTick] = useState(0);
  const hasLive = tasks.some((t) => t.endMs == null);
  useEffect(() => {
    if (!hasLive) return;
    const id = setInterval(() => setTick((n) => n + 1), 500);
    return () => clearInterval(id);
  }, [hasLive]);

  return (
    <section className="dw-card">
      <header className="dw-card-head dw-tasks-head">
        <span>Tasks</span>
        {tasks.length > 0 && (
          <button className="dw-tasks-clear" title="clear tasks" onClick={clearTasks}>
            🗑
          </button>
        )}
      </header>
      {tasks.length === 0 ? (
        <div className="dw-card-body dw-muted">idle</div>
      ) : (
        <ul className="dw-tasks">
          {tasks.map((t) => {
            const age = ageMs(t);
            const clickable = t.embedId != null;
            return (
              <li
                key={t.id}
                className={`dw-task ${statusClass(t)}${clickable ? " dw-task-link" : ""}`}
                onClick={clickable ? () => scrollToEmbed(t.embedId) : undefined}
                title={clickable ? "scroll to this embed" : undefined}
              >
                <span className="dw-task-dot" />
                <span className="dw-task-src">{taskName(t)}</span>
                <span className="dw-task-age">{fmtMs(age)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
